import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '@/lib/api'
import { formatPhoneDisplay } from '@/lib/utils'
import { format, formatDistanceToNow } from 'date-fns'
import {
  ArrowLeft, Phone, Mail, Calendar, MessageSquare, Briefcase, Gauge, CalendarDays,
  PhoneIncoming, PhoneForwarded, Globe, StickyNote, Loader2, ChevronRight, Trash2,
  Trophy, XCircle, ChevronDown, Check, Pencil, X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Select as UiSelect,
  SelectContent as UiSelectContent,
  SelectItem as UiSelectItem,
  SelectTrigger as UiSelectTrigger,
  SelectValue as UiSelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'

type LeadSource = 'inbound_call' | 'outbound_call' | 'chat' | 'web_form'
type LeadStatus = 'new' | 'contacted' | 'booked' | 'qualified' | 'won' | 'lost'

interface Lead {
  id: string
  name: string | null
  email: string | null
  phone: string | null
  caseType: string | null
  inquiryDetails: string | null
  leadScore: number | null
  source: LeadSource
  sourceMeta: Record<string, any>
  status: LeadStatus
  firstSeenAt: string
  lastActivityAt: string
  createdAt: string
}

interface TimelineItem {
  kind: string
  at: string
  summary: string
  meta: Record<string, any>
  refId?: string
}

const STATUS_META: Record<LeadStatus, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-brand-periwinkle/15 text-brand-royal dark:text-brand-periwinkle' },
  contacted: { label: 'Contacted', className: 'bg-slate-500/15 text-slate-700 dark:text-slate-300' },
  booked: { label: 'Booked', className: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  qualified: { label: 'Qualified', className: 'bg-violet-500/15 text-violet-700 dark:text-violet-400' },
  won: { label: 'Won', className: 'bg-green-500/15 text-green-700 dark:text-green-400' },
  lost: { label: 'Lost', className: 'bg-rose-500/15 text-rose-700 dark:text-rose-400' },
}

const TIMELINE_ICON: Record<string, { icon: typeof Phone; className: string }> = {
  inbound_call: { icon: PhoneIncoming, className: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' },
  outbound_call: { icon: PhoneForwarded, className: 'text-sky-600 bg-sky-50 dark:bg-sky-900/20' },
  chat: { icon: MessageSquare, className: 'text-violet-600 bg-violet-50 dark:bg-violet-900/20' },
  web_form: { icon: Globe, className: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' },
  booking: { icon: Calendar, className: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' },
  note: { icon: StickyNote, className: 'text-slate-600 bg-slate-100 dark:bg-slate-800' },
  status_changed: { icon: ChevronRight, className: 'text-slate-500 bg-slate-100 dark:bg-slate-800' },
  case_type_changed: { icon: ChevronRight, className: 'text-slate-500 bg-slate-100 dark:bg-slate-800' },
}

function displayName(lead: Lead): string {
  return lead.name || lead.email || (lead.phone ? formatPhoneDisplay(lead.phone) : 'Unknown lead')
}

const CASE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '__clear__', label: '— None —' },
  { value: 'family', label: 'Family' },
  { value: 'personal_injury', label: 'Personal Injury' },
  { value: 'criminal', label: 'Criminal Defense' },
  { value: 'estate', label: 'Estate Planning' },
  { value: 'immigration', label: 'Immigration' },
  { value: 'employment', label: 'Employment' },
  { value: 'business', label: 'Business' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'bankruptcy', label: 'Bankruptcy' },
]

function caseTypeLabel(value: string | null): string {
  if (!value) return '—'
  const match = CASE_TYPE_OPTIONS.find(o => o.value === value)
  return match?.label || value
}

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [lead, setLead] = useState<Lead | null>(null)
  const [timeline, setTimeline] = useState<TimelineItem[]>([])
  const [loading, setLoading] = useState(true)
  const [note, setNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Inline edit state — one active field at a time
  const [editingField, setEditingField] = useState<'name' | 'caseType' | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [savingEdit, setSavingEdit] = useState(false)

  // Transcript expansion state (keyed by conversation refId)
  const [expandedChatId, setExpandedChatId] = useState<string | null>(null)
  const [chatMessages, setChatMessages] = useState<Record<string, Array<{ id: string; sender_type: string; content: string; timestamp: string }>>>({})
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null)

  const load = async () => {
    if (!id) return
    setLoading(true)
    try {
      const [leadRes, timelineRes] = await Promise.all([
        api.get(`/api/leads/${id}`),
        api.get(`/api/leads/${id}/timeline`),
      ])
      setLead(leadRes.data.lead)
      setTimeline(timelineRes.data.items || [])
    } catch (err: any) {
      toast({
        title: 'Failed to load lead',
        description: err?.response?.data?.message || 'Try returning to the leads list.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleStatusChange = async (status: LeadStatus) => {
    if (!lead || status === lead.status) return
    setSavingStatus(true)
    try {
      const res = await api.patch(`/api/leads/${lead.id}/status`, { status })
      setLead(res.data.lead)
      await load()
      toast({ title: 'Status updated' })
    } catch (err: any) {
      toast({
        title: 'Failed to update status',
        description: err?.response?.data?.message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSavingStatus(false)
    }
  }

  const startEdit = (field: 'name' | 'caseType') => {
    if (!lead) return
    setEditingField(field)
    setEditValue(field === 'name' ? (lead.name || '') : (lead.caseType || ''))
  }

  const cancelEdit = () => {
    setEditingField(null)
    setEditValue('')
  }

  const saveEdit = async () => {
    if (!lead || !editingField) return
    setSavingEdit(true)
    try {
      const field = editingField
      // Dropdown uses '__clear__' sentinel to clear to null (Select can't take "")
      const raw = editValue === '__clear__' ? null : editValue
      const payload: Record<string, any> = { [field]: raw }
      const res = await api.patch(`/api/leads/${lead.id}`, payload)
      setLead(res.data.lead)
      await load()
      setEditingField(null)
      setEditValue('')
      toast({ title: 'Saved' })
    } catch (err: any) {
      toast({
        title: 'Failed to save',
        description: err?.response?.data?.message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSavingEdit(false)
    }
  }

  const toggleChatTranscript = async (conversationId: string) => {
    if (!lead) return
    if (expandedChatId === conversationId) {
      setExpandedChatId(null)
      return
    }
    setExpandedChatId(conversationId)
    if (chatMessages[conversationId]) return
    setLoadingChatId(conversationId)
    try {
      const res = await api.get(`/api/leads/${lead.id}/conversations/${conversationId}/messages`)
      setChatMessages(prev => ({ ...prev, [conversationId]: res.data.messages || [] }))
    } catch (err: any) {
      toast({
        title: 'Failed to load transcript',
        description: err?.response?.data?.message || 'Please try again.',
        variant: 'destructive',
      })
      setExpandedChatId(null)
    } finally {
      setLoadingChatId(null)
    }
  }

  const handleDelete = async () => {
    if (!lead) return
    setDeleting(true)
    try {
      await api.delete(`/api/leads/${lead.id}`)
      toast({ title: 'Lead deleted' })
      navigate('/leads')
    } catch (err: any) {
      toast({
        title: 'Failed to delete lead',
        description: err?.response?.data?.message || 'Please try again.',
        variant: 'destructive',
      })
      setDeleting(false)
      setDeleteConfirmOpen(false)
    }
  }

  const handleSaveNote = async () => {
    if (!lead || !note.trim()) return
    setSavingNote(true)
    try {
      await api.post(`/api/leads/${lead.id}/note`, { note: note.trim() })
      setNote('')
      await load()
    } catch (err: any) {
      toast({
        title: 'Failed to save note',
        description: err?.response?.data?.message || 'Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSavingNote(false)
    }
  }

  if (loading && !lead) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!lead) {
    return (
      <div className="p-8">
        <Button variant="ghost" onClick={() => navigate('/leads')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to leads
        </Button>
        <p className="mt-8 text-muted-foreground">Lead not found.</p>
      </div>
    )
  }

  const statusMeta = STATUS_META[lead.status]

  return (
    <div className="flex flex-col gap-5 sm:gap-6 pb-12">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} className="-ml-2">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to leads
        </Button>
      </div>

      {/* Header */}
      <div className="relative rounded-2xl border bg-card shadow-sm card-inset hero-card">
        <div className="hero-card-orbs" aria-hidden="true" />
        <div className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start gap-4 justify-between">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-indigo to-brand-royal flex items-center justify-center text-white font-semibold text-lg shadow-sm shrink-0">
              {(lead.name?.[0] || lead.email?.[0] || '?').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              {editingField === 'name' ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit() }}
                    placeholder="Full name"
                    className="text-lg font-semibold h-9 max-w-sm"
                    autoFocus
                    disabled={savingEdit}
                  />
                  <Button size="sm" onClick={saveEdit} disabled={savingEdit}>
                    {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={savingEdit}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => startEdit('name')}
                  className="group inline-flex items-center gap-2 text-2xl font-semibold text-foreground truncate hover:text-brand-royal transition-colors text-left"
                  title="Click to edit name"
                >
                  <span className="truncate">{displayName(lead)}</span>
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground/50 group-hover:text-brand-royal transition-colors opacity-0 group-hover:opacity-100" />
                </button>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                {lead.phone && (
                  <span className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    <a href={`tel:${lead.phone}`} className="hover:text-foreground">{formatPhoneDisplay(lead.phone)}</a>
                  </span>
                )}
                {lead.email && (
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    <a href={`mailto:${lead.email}`} className="hover:text-foreground">{lead.email}</a>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={`${statusMeta.className} text-xs font-medium px-2.5 py-1`}>
              {statusMeta.label}
            </Badge>

            {/* Terminal-outcome quick actions. Only show when the lead isn't
                already in that state — keeps the row uncluttered once decided. */}
            {lead.status !== 'won' && (
              <Button
                size="sm"
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50 hover:text-green-800 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-950/40"
                onClick={() => handleStatusChange('won')}
                disabled={savingStatus}
              >
                <Trophy className="w-3.5 h-3.5 mr-1.5" />
                Mark won
              </Button>
            )}
            {lead.status !== 'lost' && (
              <Button
                size="sm"
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:border-rose-900 dark:text-rose-400 dark:hover:bg-rose-950/40"
                onClick={() => handleStatusChange('lost')}
                disabled={savingStatus}
              >
                <XCircle className="w-3.5 h-3.5 mr-1.5" />
                Mark lost
              </Button>
            )}

            {/* All-status dropdown for the in-between transitions.
                Separated from the won/lost actions so they don't read as
                a "change to another pending state" click. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" disabled={savingStatus}>
                  Change status
                  <ChevronDown className="w-3.5 h-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  Set lead status
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(Object.keys(STATUS_META) as LeadStatus[]).map(s => (
                  <DropdownMenuItem
                    key={s}
                    onClick={() => handleStatusChange(s)}
                    disabled={s === lead.status}
                    className="flex items-center justify-between"
                  >
                    <span>{STATUS_META[s].label}</span>
                    {s === lead.status && <Check className="w-3.5 h-3.5 text-muted-foreground" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 mt-6 pt-6 border-t">
          <InfoCell
            label="Case type"
            icon={Briefcase}
            tint="slate"
            value={editingField === 'caseType' ? (
              <div className="flex items-center gap-2">
                <UiSelect value={editValue || '__clear__'} onValueChange={setEditValue} disabled={savingEdit}>
                  <UiSelectTrigger className="h-8 w-[180px]"><UiSelectValue /></UiSelectTrigger>
                  <UiSelectContent>
                    {CASE_TYPE_OPTIONS.map(o => (
                      <UiSelectItem key={o.value} value={o.value}>{o.label}</UiSelectItem>
                    ))}
                  </UiSelectContent>
                </UiSelect>
                <Button size="sm" onClick={saveEdit} disabled={savingEdit} className="h-8 px-2">
                  {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={savingEdit} className="h-8 px-2">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <button
                onClick={() => startEdit('caseType')}
                className="group inline-flex items-center gap-1.5 hover:text-brand-royal transition-colors"
                title="Click to edit case type"
              >
                <span>{caseTypeLabel(lead.caseType)}</span>
                <Pencil className="w-3 h-3 text-muted-foreground/50 group-hover:text-brand-royal transition-colors opacity-0 group-hover:opacity-100" />
              </button>
            )}
          />
          <InfoCell
            label="Lead score"
            icon={Gauge}
            tint={lead.leadScore != null ? scoreTint(lead.leadScore) : 'slate'}
            value={lead.leadScore != null
              ? (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${scorePillClass(lead.leadScore)}`}>
                  {lead.leadScore}/100
                </span>
              )
              : '—'}
          />
          <InfoCell
            label="Source"
            icon={sourceIcon(lead.source)}
            tint={sourceTint(lead.source)}
            value={sourceLabel(lead.source)}
          />
          <InfoCell
            label="First seen"
            icon={CalendarDays}
            tint="slate"
            value={format(new Date(lead.firstSeenAt), 'MMM d, yyyy')}
          />
        </div>

        {lead.inquiryDetails && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
              <StickyNote className="w-3 h-3" />
              Summary
            </p>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{lead.inquiryDetails}</p>
          </div>
        )}
        </div>
      </div>

      {/* Timeline */}
      <div className="border rounded-lg bg-card">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold text-foreground">Timeline</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Everything we know about this person, in order.</p>
        </div>

        {timeline.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No activity yet.
          </div>
        ) : (
          /* Editorial timeline — vertical rule connects events into a
             single visual thread. Replaces the divide-y "list of cards"
             that read as a wall of identical paragraphs. */
          <div className="relative px-6 py-5">
            {timeline.map((item, i) => {
              const iconMeta = TIMELINE_ICON[item.kind] || { icon: StickyNote, className: 'text-slate-500 bg-slate-100 dark:bg-slate-800' }
              const Icon = iconMeta.icon
              const isChat = item.kind === 'chat' && !!item.refId
              const isExpanded = isChat && expandedChatId === item.refId
              const href = !isChat ? timelineItemHref(item) : null
              const isLast = i === timeline.length - 1

              const row = (
                <>
                  {/* Vertical rule connecting icons — stops at last item.
                      Sits behind the icon disc via z-0. */}
                  {!isLast && (
                    <div
                      aria-hidden="true"
                      className="absolute left-[19px] top-9 w-px bg-border/60"
                      style={{ height: 'calc(100% - 1.5rem)' }}
                    />
                  )}
                  <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center shrink-0 ring-[3px] ring-card ${iconMeta.className}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-0.5">
                      <p className="text-sm font-medium text-foreground leading-snug">{item.summary}</p>
                      <time className="text-xs text-muted-foreground tabular-nums shrink-0">
                        {formatDistanceToNow(new Date(item.at), { addSuffix: true })}
                      </time>
                    </div>
                    {renderTimelineMeta(item)}
                    {isChat && (
                      <p className="text-xs text-brand-royal mt-1.5 group-hover:underline flex items-center gap-1">
                        {isExpanded ? 'Hide transcript' : 'View transcript'}
                        <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </p>
                    )}
                    {href && (
                      <p className="text-xs text-brand-royal mt-1.5 group-hover:underline">
                        Open {linkLabel(item.kind)} →
                      </p>
                    )}
                  </div>
                </>
              )

              return (
                <div key={`${item.kind}-${item.refId || i}`} className="relative">
                  {isChat ? (
                    <button
                      onClick={() => toggleChatTranscript(item.refId!)}
                      className="group w-full flex gap-4 pb-5 text-left transition-opacity hover:opacity-80"
                    >
                      {row}
                    </button>
                  ) : href ? (
                    <button
                      onClick={() => navigate(href)}
                      className="group w-full flex gap-4 pb-5 text-left transition-opacity hover:opacity-80"
                    >
                      {row}
                    </button>
                  ) : (
                    <div className="flex gap-4 pb-5">
                      {row}
                    </div>
                  )}

                  {isExpanded && item.refId && (
                    <div className="-mx-6 px-6 pb-5 mb-5 bg-muted/20 border-y border-border/40">
                      <div className="pl-12 pr-2 pt-3">
                        {loadingChatId === item.refId && !chatMessages[item.refId] ? (
                          <div className="py-6 flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : chatMessages[item.refId]?.length ? (
                          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                            {chatMessages[item.refId].map(msg => {
                              const isVisitor = msg.sender_type === 'visitor' || msg.sender_type === 'user'
                              return (
                                <div key={msg.id} className={`flex ${isVisitor ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                                    isVisitor
                                      ? 'bg-brand-royal/10 text-foreground'
                                      : 'bg-card border text-foreground'
                                  }`}>
                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                      {isVisitor ? 'Visitor' : 'AI'} · {format(new Date(msg.timestamp), 'h:mm a')}
                                    </p>
                                    <p className="whitespace-pre-wrap">{msg.content}</p>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <p className="py-4 text-sm text-muted-foreground">No messages to show.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Add note */}
      <div className="border rounded-lg p-6 bg-card">
        <h2 className="font-semibold text-foreground mb-3">Add a note</h2>
        <Textarea
          placeholder="Internal note — only your team sees this…"
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
        />
        <div className="flex justify-end mt-3">
          <Button onClick={handleSaveNote} disabled={!note.trim() || savingNote}>
            {savingNote && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save note
          </Button>
        </div>
      </div>

      {/* Delete zone — low-visual-weight so it doesn't read as a primary action */}
      <div className="flex justify-end pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => setDeleteConfirmOpen(true)}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete lead
        </Button>
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={(open) => !open && !deleting && setDeleteConfirmOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this lead?</DialogTitle>
            <DialogDescription>
              Removes {displayName(lead)} from Leads. Any calls, chats, or bookings attached to them stay in the system — they just won't be grouped here anymore. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function InfoCell({ label, value, icon: Icon, tint = 'slate' }: {
  label: string
  value: React.ReactNode
  icon?: React.ComponentType<{ className?: string }>
  tint?: 'slate' | 'emerald' | 'sky' | 'violet' | 'amber' | 'rose'
}) {
  const tintMap: Record<string, { bg: string; text: string }> = {
    slate: { bg: 'bg-slate-100 dark:bg-slate-800/60', text: 'text-slate-600 dark:text-slate-300' },
    emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
    sky: { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-600 dark:text-sky-400' },
    violet: { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-600 dark:text-violet-400' },
    amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
    rose: { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-600 dark:text-rose-400' },
  }
  const t = tintMap[tint]
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${t.bg}`}>
          <Icon className={`w-4 h-4 ${t.text}`} />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
        <div className="text-sm text-foreground">{value}</div>
      </div>
    </div>
  )
}

function sourceTint(source: LeadSource): 'emerald' | 'sky' | 'violet' | 'amber' {
  switch (source) {
    case 'inbound_call': return 'emerald'
    case 'outbound_call': return 'sky'
    case 'chat': return 'violet'
    case 'web_form': return 'amber'
  }
}

function sourceIcon(source: LeadSource): React.ComponentType<{ className?: string }> {
  switch (source) {
    case 'inbound_call': return PhoneIncoming
    case 'outbound_call': return PhoneForwarded
    case 'chat': return MessageSquare
    case 'web_form': return Globe
  }
}

function scoreTint(score: number): 'emerald' | 'amber' | 'rose' {
  if (score >= 70) return 'emerald'
  if (score >= 40) return 'amber'
  return 'rose'
}

function scorePillClass(score: number): string {
  if (score >= 70) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
  if (score >= 40) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
  return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
}

function timelineItemHref(item: TimelineItem): string | null {
  if (!item.refId) return null
  switch (item.kind) {
    case 'inbound_call':
    case 'outbound_call':
      return `/calls?callId=${item.refId}`
    case 'chat':
      return `/conversations?sessionId=${item.refId}`
    case 'booking':
      return `/bookings?bookingId=${item.refId}`
    // No detail route exists for individual web-form submissions —
    // /form-connections is for *configuring* form integrations, not for
    // viewing payloads. Render the captured fields inline (see
    // renderTimelineMeta) instead of pointing at a wrong destination.
    case 'web_form':
      return null
    default:
      return null
  }
}

function linkLabel(kind: string): string {
  switch (kind) {
    case 'inbound_call':
    case 'outbound_call':
      return 'call'
    case 'chat':
      return 'chat transcript'
    case 'booking':
      return 'booking'
    case 'web_form':
      return 'submission'
    default:
      return 'source'
  }
}

function sourceLabel(source: LeadSource): string {
  switch (source) {
    case 'inbound_call': return 'Inbound call'
    case 'outbound_call': return 'Outbound call'
    case 'chat': return 'Chat'
    case 'web_form': return 'Web form'
  }
}

function renderTimelineMeta(item: TimelineItem) {
  if (item.kind === 'inbound_call' || item.kind === 'outbound_call') {
    const duration = item.meta?.duration
    const score = item.meta?.qualificationScore
    return (
      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
        {duration != null && <span>{Math.round(duration)}s</span>}
        {score != null && <span>Score: {score}/100</span>}
        {item.meta?.sentiment && <span>Sentiment: {item.meta.sentiment}</span>}
      </div>
    )
  }
  if (item.kind === 'booking') {
    return (
      <div className="text-xs text-muted-foreground mt-1">
        {item.meta?.scheduledDate} {item.meta?.scheduledTime}
      </div>
    )
  }
  if (item.kind === 'note' && typeof item.meta?.authorEmail === 'string') {
    return <div className="text-xs text-muted-foreground mt-1">by {item.meta.authorEmail}</div>
  }
  if (item.kind === 'web_form') {
    const m = item.meta || {}
    const fields: Array<[string, string]> = []
    if (m.name) fields.push(['Name', String(m.name)])
    if (m.email) fields.push(['Email', String(m.email)])
    if (m.phone) fields.push(['Phone', String(m.phone)])
    if (m.status && m.status !== 'completed') fields.push(['Status', String(m.status)])
    if (fields.length === 0) return null
    return (
      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
        {fields.map(([k, v]) => (
          <span key={k}>
            <span className="text-muted-foreground/70">{k}:</span> {v}
          </span>
        ))}
      </div>
    )
  }
  return null
}
